<template>
  <b-modal id="agreement-modal" size="md" title="Agreement Summary" hide-footer>
    <template #modal-title>
      <div class="agreement-modal-title">
        <span class="title-label">
          Agreement Summary
        </span>
      </div>
    </template>
    <div v-if="!agreement" class="agreement-empty">
      <p class="empty-text">
        The vessel is not serviced under an agreement with a distributor. Payment is only possible via Stripe.
      </p>

      <b-button @click="$router.push('distributors')" size="sm" variant="info" block class="choose-button">
        Choose a distributor
      </b-button>
    </div>
    <div v-else class="agreement-card">
      <div class="agreement-header">
        <div>
          <div class="agreement-title">
            {{ agreement.name }}
          </div>
          <div class="agreement-subtitle">
            Agreement ID #{{ agreement.displayId }}
          </div>
        </div>
        <span :class="['status-chip', isExpired ? 'status-expired' : 'status-active']">
          {{ isExpired ? 'Expired' : 'Active' }}
        </span>
      </div>

      <div class="agreement-section">
        <div class="section-grid">
          <div class="section-field">
            <span class="field-label">Currency</span>
            <span class="field-value">{{ agreement.currency || '—' }}</span>
          </div>
          <div class="section-field">
            <span class="field-label">Valid till</span>
            <span
                class="field-value"
                :class="{ 'value-danger': isExpired }"
            >
              {{ formatDate(agreement.validTill) }}
            </span>
          </div>
        </div>
      </div>

      <div class="agreement-section">
        <div class="section-label">
          Services
        </div>
        <div class="service-list">
          <!-- Группа 1: Всегда включенные сервисы -->
          <div 
            v-for="service in alwaysActiveServices" 
            :key="service.name" 
            class="service-item is-available"
          >
            <span class="service-icon">
              <i class="fas fa-check-circle" />
            </span>
            <div class="service-copy">
              <span class="service-name">{{ service.name }}</span>
              <span class="service-meta">Valid till {{ formatDate(agreement.validTill) }}</span>
            </div>
          </div>

          <!-- Группа 2: Опциональные сервисы -->
          <div v-for="service in optionalServices" :key="service.key" :class="['service-item', getServiceClass(service)]">
            <span class="service-icon">
              <i :class="getServiceIcon(service)" />
            </span>
            <div class="service-copy">
              <span class="service-name">{{ service.name }}</span>
              <span v-if="service.isActive" class="service-meta">
                Valid till {{ formatDate(service.endDate) }}
              </span>
              <span v-else class="service-meta">
                <a 
                  v-if="!requestSentServices[service.key]"
                  href="#" 
                  @click.prevent="requestService(service)"
                  class="service-meta-link"
                >
                  request the service...
                </a>
                <span v-else class="service-request-sent">
                  <i class="fas fa-check-circle mr-1" /> Request sent
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { getDeviceServices, requestService } from '@/helpers/apiServices'
import { useMomentaryStore } from '@/stores/momentary'

export default {
  props: {
    agreement: {
      type: Object,
      default: null,
    },
    deviceId: {
      type: String,
      default: null,
    },
    ship: {
      type: Object,
      default: null,
    },
    userData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      deviceServices: [],
      loading: false,
      requestSentServices: {}, // Отслеживаем какие сервисы уже запрошены {serviceKey: true}
    }
  },
  computed: {
    // Проверка, истек ли срок действия договора
    isExpired() {
      if (!this.agreement || !this.agreement.validTill) return false
      return new Date(this.agreement.validTill) < new Date()
    },

    // Сервисы которые всегда активны
    alwaysActiveServices() {
      return [
        { name: 'Primar ENC' },
        { name: 'Routes' },
      ]
    },

    // Опциональные сервисы (могут быть включены/отключены)
    // Сортируем по названию
    optionalServices() {
      const servicesList = [
        { key: 'AIS-Ship', name: 'AIS Ship' },
        { key: 'AIS-Around', name: 'AIS Around' },
        { key: 'Navarea', name: 'Navarea' },
        { key: 'Weather', name: 'Weather' },
        { key: 'Passage-Plan', name: 'Passage Plan' },
        { key: 'Messengers', name: 'Messengers' },
      ]

      // Создаем Map активных сервисов с их данными (EndDate, Status и т.д.)
      const activeServicesMap = new Map(
        this.deviceServices
          .filter(s => s.Status === 'Active')
          .map(s => [s.ServiceType, s])
      )

      // Добавляем информацию об активности и дату окончания из API
      return servicesList
        .map(service => {
          const apiService = activeServicesMap.get(service.key)
          return {
            ...service,
            isActive: !!apiService,
            endDate: apiService?.EndDate, // Дата окончания сервиса из API
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    },
  },
  watch: {
    agreement() {
      this.loadDeviceServices()
    },
    deviceId() {
      this.loadDeviceServices()
    },
  },
  mounted() {
    this.loadDeviceServices()
  },
  methods: {
    // Форматирование даты в удобочитаемый формат
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    // Загрузить сервисы устройства
    async loadDeviceServices() {
      if (!this.deviceId) {
        this.deviceServices = []
        return
      }

      this.loading = true
      try {
        const services = await getDeviceServices(this.deviceId)
        this.deviceServices = Array.isArray(services) ? services : []
      } catch (error) {
        console.error('[AgreementModal] Failed to load device services:', error)
        this.deviceServices = []
      } finally {
        this.loading = false
      }
    },

    // Получить класс CSS для сервиса
    getServiceClass(service) {
      if (service.isActive) {
        return 'is-available'
      }
      return 'is-missing'
    },

    // Получить иконку для сервиса
    getServiceIcon(service) {
      if (service.isActive) {
        return 'fas fa-check-circle'
      }
      return 'fas fa-times-circle'
    },

    // Обработать клик по ссылке для включения сервиса
    async requestService(service) {
      // Получаем store экземпляр локально
      const momentaryStore = useMomentaryStore()
      const shipInfo = momentaryStore.getSelectedShipInfo
      
      // Вызываем функцию для отправки запроса сервиса
      await requestService(service.key, service.name, shipInfo)
      
      // Добавляем сервис в Object запрошенных сервисов (Vue 2 реактивность)
      this.$set(this.requestSentServices, service.key, true)
    },
  },
}
</script>

<style scoped>
.agreement-empty {
  padding: 1.75rem 1.25rem;
  text-align: center;
  color: #4a5266;
}

.empty-text {
  margin-bottom: 1.25rem;
  font-size: 0.92rem;
}

.choose-button {
  font-weight: 600;
  letter-spacing: 0.01em;
}

.agreement-card {
  background: #ffffff;
  border: 1px solid #e5e8f0;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(23, 30, 56, 0.14);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

::v-deep .modal-header {
  background: linear-gradient(135deg, #f4f6ff 0%, #e6e9ff 100%);
  border-bottom: none;
  padding: 18px 22px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 -1px 0 rgba(98, 111, 149, 0.12);
}

::v-deep .modal-header .close {
  color: #4b5fdd;
  opacity: 0.75;
  text-shadow: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

::v-deep .modal-header .close:hover {
  opacity: 1;
  transform: scale(1.05);
}

.agreement-modal-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.title-label {
  color: #1f2940;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.title-meta {
  color: #5b6480;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.agreement-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.agreement-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f2940;
  letter-spacing: 0.01em;
}

.agreement-subtitle {
  color: #6a738c;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-top: 4px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: inset 0 0 0 1px rgba(98, 111, 149, 0.1);
}

.status-active {
  background: linear-gradient(135deg, #f1f7f4, #e3f5ea);
  color: #1f7a4d;
}

.status-expired {
  background: #ffe5ea;
  color: #a90021;
}

.agreement-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-label {
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: #6a738c;
}

.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
}

.section-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  color: #6a738c;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.field-value {
  color: #2a3144;
  font-size: 0.88rem;
  font-weight: 500;
}

.field-value.value-danger {
  color: #a90021;
}

.service-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.service-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid rgba(229, 232, 240, 0.9);
  background: linear-gradient(135deg, #fbfcff, #f6f8ff);
  box-shadow: 0 6px 16px rgba(20, 29, 56, 0.06);
}

.service-item.is-available {
  border-color: rgba(58, 150, 104, 0.4);
}

.service-item.needs-action {
  border-color: rgba(75, 95, 221, 0.35);
}

.service-item.is-missing {
  border-color: rgba(214, 68, 95, 0.35);
}

.service-icon {
  font-size: 1rem;
  color: #4b5fdd;
  margin-top: 1px;
}

.service-item.is-available .service-icon {
  color: #2f9c68;
}

.service-item.is-missing .service-icon {
  color: #d6445f;
}

.service-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.service-name {
  color: #1f2940;
  font-size: 0.86rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.service-meta {
  color: #67708b;
  font-size: 0.8rem;
}

.service-meta a {
  color: #3a4256;
  border-bottom: 1px dashed rgba(37, 45, 64, 0.5);
  padding-bottom: 1px;
  transition: color 0.2s ease;
  text-decoration: none;
}

.service-meta a:hover {
  color: #161b28;
  text-decoration: none;
}

.service-meta-link {
  color: #3a4256;
  border-bottom: 1px dashed rgba(37, 45, 64, 0.5);
  padding-bottom: 1px;
  transition: color 0.2s ease;
  text-decoration: none;
  cursor: pointer;
}

.service-meta-link:hover {
  color: #161b28;
  text-decoration: none;
}

.service-request-sent {
  color: #22c55e;
  font-weight: 500;
}

@media (max-width: 576px) {
  .agreement-card {
    padding: 16px;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }
}
</style>
